<!--
  The editor of an entity, as a button-group in a orderable-item,
  with a slidedown

  Props:
  - value: the value
    ex: {
          name: "entity_name",
          default: "entity default value script",
          entity: "entityid",
          required: true || false,
          questions: ["question for the default value", ...],
        }
  - other: a value to compare with
  - newInput: true for a new input, with only the entity name enabled
  - header: true for displaying only the "table" headers
  - entitiesOptions: an array of available entities
  - related: true if the entity is used in an input
      it thus cannot be deleted, and must have a type
  - duplicate: true if the entity name is already used
      an error is then displayed on validation
  - deletable: true if the entty can be disabled despite being related
      because it is a duplicate
  - active: true if the slidedown should be opened
  - prop: the prop prefix to use for el-form-item
  - rules: el-form-item additional rules
  - newFocus: if should be focused on creation
  - placeholder: a placeholder for an empty entity name
  - localVariables: a list of exsting local variables
      for default value autocompletion
  - contextVariables: a list of exsting context variables
      for default value autocompletion

  Emits:
  - input: input
  - submit: for a new item, when it has been submitted
  - delete: when the delete button is pressed
  - move: when the item has been drag&dropped
  - activate: when the item is clicked
  - update:name: when the name of the entity has been changed
  - update:ref: when the type of the entity has been changed
-->

<template>
  <comparator-filter>
    <template v-slot="{ compare }">
      <!-- orderable -->
      <orderable-item
        :disabled="header || compare"
        :new-item="newInput"
        v-bind="$attrs"
        v-on="$listeners"
        @click.native="handleActivate">
        <!-- header tiles -->
        <input-headers-group v-if="header">
          <input-header width="119px">More</input-header>
          <input-header>Entity name</input-header>
          <input-header>
            Default value
            <template slot="tooltip">
              Used when the current value is <code>null</code>.<br/>
              A comma separated list of values, the first non <code>null</code> value is chosen.<br/>
              If the <em>default value</em> is <code>null</code> too and the entity is <em>required</em>, the value will be asked to the user.<br/>
              Example: <code>$variable, @condition ? $other : null, "value"</code>
            </template>
          </input-header>
          <input-header width="56px"></input-header>
        </input-headers-group>
        <!-- general case, a button group with a slidedown -->
        <template v-else>
          <el-form-errors>
            <!-- the button group -->
            <el-button-group style="width: 100%">
              <!-- controls the slidedown -->
              <tooltip-button v-if="newInput"
                placement="bottom-start">
                You can create a new <em>entity</em> to assign it a default value, <br/>
                to make it <em>required</em> and ask the value to the user, <br/>
                or to signal an intent parameter to follow.
              </tooltip-button>
              <el-button v-else
                style="padding: 12px" type="info"
                :icon="!formValid ? 'el-icon-error' : active ? 'el-icon-caret-top' : 'el-icon-caret-bottom'"
                @click.stop="handleActivate(!active && formValid)"
                :tabindex="-1">
              </el-button>
              <!-- button to reorder the items -->
              <orderable-item-button></orderable-item-button>
              <!-- checkbox for required input -->
              <comparable prop="value.required" :default="false" diff>
                <template v-slot="{ status, compare }">
                  <el-checkbox border class="el-checkbox--big"
                    :disabled="newInput || compare"
                    :class="'comparable-' + status"
                    v-model="copy.required"></el-checkbox>
                </template>
              </comparable>
              <!-- entity name input -->
              <comparable prop="value.name" default=""
                :required="!newInput" :rules="computedRules"
                @input="handleNameUpdated">
                <template v-slot="{ attrs, listeners, status, view, compare }">
                  <el-form-small-item ref="name"
                    :class="'comparable-' + status"
                    :prop="view && prop && prop + '.name'"
                    v-bind="attrs"
                    field="name">
                    <el-entity-input
                      :value="copy.name"
                      type="entity"
                      v-on="listeners"
                      :disabled="compare"
                      :placeholder="placeholder || 'Entity name'">
                    </el-entity-input>
                  </el-form-small-item>
                </template>
              </comparable>
              <!-- entity default value input -->
              <comparable prop="value.value" default="">
                <template v-slot="{ status, view, compare }">
                  <el-form-item-code-input :class="'comparable-' + status"
                    ref="value"
                    :prop="view && prop && prop + '.value'"
                    :disabled="newInput || compare"
                    v-model="copy.value"
                    field="default value"
                    :show-message="true"
                    :varstatus="varstatus"
                    placeholder="default value"
                    clearable></el-form-item-code-input>
                </template>
              </comparable>
              <!-- delete button -->
              <el-button
                :icon="newInput ? 'el-icon-circle-plus-outline' : 'el-icon-delete'"
                @click="$emit('delete')"
                :tabindex="-1"
                :disabled="newInput || compare || related && !deletable">
              </el-button>
            </el-button-group>
          </el-form-errors>
          <!-- the slidedown -->
          <el-collapse-transition v-if="!newInput">
            <el-form-sub
              v-show="active || !formValid"
              :prop="prop"
              :model="value"
              @valid="val => formValid = val"
              label-width="120px"
              :hide-required-asterisk="true">
              <fieldset>
                <legend>requirements</legend>
                <comparator
                  :view="copy"
                  :compare="other">
                  <template v-slot="{ value }">
                    <comparable prop="entity" :default="null"
                      :required="related || copy.required"
                      @input="handleRefUpdated">
                      <template
                        v-slot="{ compare, prop, attrs, listeners, status }">
                        <el-form-item :class="'comparable-' + status"
                          :prop="prop"
                          v-bind="attrs"
                          label="Type"
                          field="type">
                          <el-button-group style="max-width: 360px">
                            <!-- the entity type selector -->
                            <el-filterable-entity-select
                              :clearable="!related"
                              :value="value.entity"
                              :pre-filter="copy.name"
                              v-on="listeners"
                              :disabled="compare"
                              :options="entitiesOptions"
                              type="entity"
                              value-field="entity"
                              label-field="name">
                              <template slot="info" slot-scope="{item}">{{
                                item && !item.local ? '(global)' : null
                              }}</template>
                            </el-filterable-entity-select>
                            <router-link v-if="entitiesLinks[value.entity]"
                              style="padding: 12px"
                              :to="entitiesLinks[value.entity]"
                              target="_blank"
                              class="el-button"
                              :tabindex="-1">
                              <i class="el-icon-link"></i>
                            </router-link>
                            <el-button v-else disabled
                              style="padding: 12px"
                              icon="el-icon-link"
                              :tabindex="-1">
                            </el-button>
                            <!-- another "required" checkbox -->
                            <comparable prop="required" diff :default="false">
                              <template
                                v-slot="{ compare, status }">
                                <el-checkbox border
                                  :class="'comparable-' + status"
                                  v-model="value.required"
                                  :disabled="compare"
                                  >required
                                  <tooltip-icon placement="top">
                                    When an <em>entity</em> is <em>required</em>,<br/>
                                    its value will be asked to the user if not provided
                                  </tooltip-icon>
                                </el-checkbox>
                              </template>
                            </comparable>
                          </el-button-group>
                        </el-form-item>
                      </template>
                    </comparable>
                  </template>
                </comparator>
                <!-- a list of questions for a required entity -->
                <el-collapse-transition>
                  <el-form-item v-if="copy.required"
                    prop="questions"
                    label="Questions">
                    <comparator :view="questions" :compare="otherQuestions">
                      <template v-slot="{ value, index }">
                        <comparable prop="value"
                          :ignore="value.temporary"
                          :required="!value.temporary"
                          :rules="OutputValidator">
                          <template v-slot="{ attrs, compare, view, status }">
                            <el-form-small-item :class="'comparable-' + status"
                              :prop="view && !value.temporary && 'questions.' + index || undefined"
                              v-bind="attrs"
                              field="question">
                              <el-button-group style="width: 100%">
                                <el-response-autocomplete
                                  v-model="value.value"
                                  placeholder="Add a question for the missing value"
                                  :local-variables="localVariables"
                                  :context-variables="contextVariables"
                                  :disabled="compare"></el-response-autocomplete>
                                <el-button
                                  :icon="value.temporary ? 'el-icon-circle-plus-outline' : 'el-icon-delete'"
                                  @click="questions.splice(index, 1)"
                                  :tabindex="-1"
                                  :disabled="compare || value.temporary">
                                </el-button>
                              </el-button-group>
                            </el-form-small-item>
                          </template>
                        </comparable>
                      </template>
                    </comparator>
                  </el-form-item>
                </el-collapse-transition>
              </fieldset>
            </el-form-sub>
          </el-collapse-transition>
        </template>
      </orderable-item>
    </template>
  </comparator-filter>
</template>

<script>
import {
  VariableValidator,
  PreOutputValidator,
  OutputValidator,
} from '../../utils/validators.js'

import OrderableItem from '../orderable/OrderableItem.vue'
import OrderableItemButton from '../orderable/OrderableItemButton.vue'
import ElFormItemCodeInput from '../element/ElFormItemCodeInput.vue'
import ElFilterableEntitySelect from '../element/ElFilterableEntitySelect.vue'
import ElResponseAutocomplete from '../element/ElResponseAutocomplete.vue'
import ElEntityInput from '../element/ElEntityInput.vue'

const VAR_STATUS = {
  local: 'readonly',
  context: 'readonly',
  contexts: 'readonly',
}

export default {
  name: 'EntityEditor',

  components: {
    OrderableItem,
    OrderableItemButton,
    ElFormItemCodeInput,
    ElFilterableEntitySelect,
    ElResponseAutocomplete,
    ElEntityInput,
  },

  props: {
    value: Object,
    other: Object, // a value to compare with
    newInput: Boolean, // true for a new input
    header: Boolean, // true for displaying only the "table" headers
    entitiesOptions: Array, // an array of available entities
    entitiesLinks: Object, // a dict of entities router-link
    related: Boolean, // true if the entity is used in an input
    duplicate: Boolean, // true if the entity name is already used
    // true if the entty can be disabled despite being related
    deletable: Boolean,
    active: Boolean, // true if the slidedown should be opened
    prop: String, // the prop prefix to use for el-form-item
    rules: [Object, Array], // el-form-item additional rules
    newFocus: Boolean, // if should be focused on creation
    placeholder: String, // a placeholder for an empty entity name
    // for the default value input
    localVariables: Array,
    contextVariables: Array,
  },

  data () {
    return {
      // a copy of the expected output
      copy: {
        name: '',
        value: '',
        entity: null,
        required: false,
      },
      // an array of questions for a required entity
      questions: [],
      // for the default value input
      varstatus: VAR_STATUS,
      // if the el-sub-form is valid
      formValid: true,
      // validator for the questions
      OutputValidator: [ PreOutputValidator, OutputValidator ],
    }
  },

  computed: {
    /* merges the rules */
    computedRules () {
      return [ VariableValidator,
        {
          validator: (rule, value, callback) => callback(this.duplicate
            ? new Error('duplicated variable name') : undefined),
          trigger: 'blur',
        }].concat(this.rules || [])
    },
    /* Updates the questions array, as an array of object with keys
    and returns the new questions value, as an array of strings
    I shouldn't change questions in a computed field,
    but I'm too lazy to do something else */
    computedQuestions () {
      // not required, shouldn't have any question
      if (!this.copy.required) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        if (this.questions.length) this.questions = []
        return []
      }
      let questions = this.questions
      let last = questions.length && questions[questions.length - 1]
      // there's no temporary question anymore, add one
      if (!last || !last.temporary) {
        last = {
          key: Math.random() + '',
          value: '',
          temporary: true,
        }
        questions.push(last)
      }
      // the temporary value has been filled, add it and make a new temporary
      // it is important to check last.value, else it won't be followed
      if (last.value) {
        last.temporary = false
        questions.push({
          key: Math.random() + '',
          value: '',
          temporary: true,
        })
      }
      // build the output
      let out = []
      for (let i = 0; i < this.questions.length - 1; i++) {
        out.push(this.questions[i].value)
      }
      return out
    },
    /* Turns the other.questions array into an array of object with keys */
    otherQuestions () {
      return this.other && (this.other.questions || []).map(value => {
        return {
          key: Math.random() + '',
          value: value,
        }
      })
    },
  },
  /* init value */
  created () { this.updateValue() },
  watch: {
    /* value has changed */
    value () { this.updateValue() },
    /* copy has changed, emit it */
    copy: {
      handler () { this.valueUpdated() },
      deep: true,
    },
    /* computedQuestions has changed, emit it */
    computedQuestions () { this.valueUpdated() },
    /* remove error when not duplicate anymore */
    duplicate (val) {
      if (!val) this.$refs.name.clearValidate()
    },
  },

  methods: {
    /* emit activate when clicked somewhere */
    handleActivate (activate) {
      if (this.newInput || this.header) return
      this.$emit('activate', activate !== false)
    },
    /* emit update:name when name is changed */
    handleNameUpdated (val) {
      this.copy.name = val
      this.$nextTick(() => {})
      this.$emit('update:name', val)
    },
    /* emit update:ref when entity is changed */
    handleRefUpdated (val) {
      this.copy.entity = val
      this.$emit('update:ref', val)
    },
    /* the value has changed, update the local data */
    updateValue () {
      let value = this.value || {}
      let copy = this.copy
      let questions = this.computedQuestions
      // build a new copy if it is different than the old one
      if ((value.name || '') !== (copy.name || '') ||
        (value.value || '') !== (copy.value || '') ||
        (value.entity || null) !== (copy.entity || null) ||
        !value.required !== !copy.required) {
        this.copy = {
          name: value.name || '',
          value: value.value || '',
          entity: value.entity || null,
          required: !!value.required,
        }
      }
      // if check if answers has changed
      let same = (value.questions || []).length === questions.length
      if (same) {
        for (let i = 0; i < questions.length; i++) {
          if (value.questions[i] !== questions[i]) {
            same = false
            break
          }
        }
      }
      // update the answers if they have changed
      if (!same) {
        if (value.required) {
          this.questions = (value.questions || []).map(val => {
            return {
              key: Math.random() + '',
              value: val,
            }
          })
          this.questions.push({
            key: Math.random() + '',
            value: '',
            temporary: true,
          })
        } else this.questions = []
      }
    },
    /* the local data has changed, emit a new value */
    valueUpdated () {
      let copy = this.copy
      // build the new value
      let value = { name: copy.name }
      if (copy.value) value.value = copy.value
      if (copy.entity) value.entity = copy.entity
      if (copy.required) value.required = true
      if (copy.required) value.questions = this.computedQuestions
      // emit it
      this.$emit('input', value)
    },
    /* focus the right input */
    focus () {
      this.$refs.value.focus()
      this.handleActivate()
    },
  },
  /* focus if newFocus */
  mounted () {
    if (this.newFocus) this.focus()
  },
}
</script>
